import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  changeCriteriaScoreStatus,
  createCriteriaScore,
  deleteCriteriaScore,
  getCriteriaScores,
  reorderCriteriaScores,
  updateCriteriaScore,
} from '../api/criteriaScoreApi'
import type {
  CriteriaScore,
  CriteriaScoreStatus,
  CriteriaScoreUpsert,
  PagedResponse,
} from '../types'

const KEY = 'activity-criteria-scores'

/**
 * Değerlendirme kriteri yönetimi sunucu-durumu (028): liste + oluştur/güncelle/durum/sil/yeniden-sırala
 * mutation'ları. Her başarıda liste invalidate edilir. Güncel kayıt cache'e yazılır (bayat modDate → 409
 * önlenir). (025 useInvitationTypes deseniyle aynı; ağırlık toplamı/uyarısı sayfada türetilir.)
 */
export function useCriteriaScores() {
  const { i18n } = useTranslation()
  const qc = useQueryClient()

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [KEY] })
  }

  const applyUpdated = (updated: CriteriaScore) =>
    qc.setQueryData<PagedResponse<CriteriaScore>>([KEY, i18n.language], (prev) =>
      prev
        ? { ...prev, content: prev.content.map((t) => (t.id === updated.id ? updated : t)) }
        : prev,
    )

  const list = useQuery({
    queryKey: [KEY, i18n.language],
    queryFn: getCriteriaScores,
  })

  // meta.handledCodes: domaine özel iş kodu ekranda ele alınır → merkezi hata toast'ı (019) susar.
  const create = useMutation({
    mutationFn: (body: CriteriaScoreUpsert) => createCriteriaScore(body),
    onSuccess: invalidate,
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: CriteriaScoreUpsert }) =>
      updateCriteriaScore(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const changeStatus = useMutation({
    mutationFn: ({ id, body }: { id: number; body: CriteriaScoreStatus }) =>
      changeCriteriaScoreStatus(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteCriteriaScore(id),
    onSuccess: invalidate,
  })

  // Yeniden sıralama: liste anında (optimistic) yeni sıraya geçer; backend 1..N yazar. Hata → geri al.
  const reorder = useMutation({
    mutationFn: (orderedIds: number[]) => reorderCriteriaScores(orderedIds),
    onMutate: async (orderedIds) => {
      await qc.cancelQueries({ queryKey: [KEY, i18n.language] })
      const prev = qc.getQueryData<PagedResponse<CriteriaScore>>([KEY, i18n.language])
      if (prev) {
        const byId = new Map(prev.content.map((r) => [r.id, r]))
        const content = orderedIds
          .map((id) => byId.get(id))
          .filter((r): r is CriteriaScore => r != null)
        qc.setQueryData<PagedResponse<CriteriaScore>>([KEY, i18n.language], { ...prev, content })
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData([KEY, i18n.language], ctx.prev)
    },
    onSettled: invalidate,
  })

  return { list, create, update, changeStatus, remove, reorder }
}
